import { assert } from '../app/logger.js';
import { listenToEvent } from '../util/listen_to_event.js';
import { window } from '../window.js';
import { AutocompleteControl } from './autocomplete_control.js';
import { HTMLControl } from './html_control.js';
import './popup_control.js';

/**
 * List control
 **/
class ListControl extends HTMLControl {
  constructor() {
    super();

    this.noValidate = false;
    this.breakOnSpace = false;

    this.onchange = null;
    this.oninput = null;
    this.onitemchange = null;
    this.onfocus = null;
    this._readonly = false;
    this._value = '';
    this.items = [];
  }

  initialize() {
    this.classList.add('editable-control-container', 'list-control-container');

    super.initialize();

    this.editableListElement.onfocus = this.onFocus.bind(this);
    this.editableListElement.onblur = this.onBlur.bind(this);

    this.autocompleteElement.onfocus = this.onInputFocus.bind(this);
    this.autocompleteElement.onblur = this.onInputBlur.bind(this);

    listenToEvent(this.autocompleteElement, 'itemselect', ev =>
      this.addItemFromSelected(ev.detail)
    );
    this.autocompleteElement.oninput = val => this.oninput && this.oninput(val);

    this.autocompleteElement.inputElement.addEventListener(
      'keydown',
      ev => this.onInputKeyDown(ev),
      false
    );
  }

  static markup = `
<span class='editable-control editable-list' tabindex='-1'>
  <span class='editable-control-label'></span>
  <span class='editable-control-value'></span>
  <autocomplete-control class='editable-control-list-input'></autocomplete-control>
</span>
`;

  static properties = {
    balloon: {
      set(v) {
        this.popupElement.innerHTML = v;
      },
    },
    label: {
      attribute: true,
      set(v) {
        this.labelElement.textContent = `${v}: `;
      },
    },
    options: {
      set(v) {
        this.setOptions(v);
      },
    },
    plainStyle: {
      attribute: {
        name: 'plain',
        type: 'boolean',
      },
      set(v) {
        this.editableListElement.setAttribute('tabindex', v ? 0 : -1);
      },
    },
    readonly: {
      attribute: 'boolean',
      set(v) {
        this.autocompleteElement.hidden = v;
        v
          ? this.editableListElement.setAttribute('readonly', 'true')
          : this.editableListElement.removeAttribute('readonly');
      },
    },
    requestItems: {
      set(v) {
        this.autocompleteElement.requestItems = v;
      },
    },
    tabable: {
      set(v) {
        this.editableListElement.setAttribute('tabindex', v ? 0 : -1);
      },
    },
    value: {
      get() {
        return this.getValue();
      },
      set(v) {
        return this.setValue(v);
      },
    },
  };

  setOptions(options) {
    this.autocompleteElement.placeholder = `e.g. ${options[0]}`;

    this.autocompleteElement.requestItems = term => {
      let vals = this.valueAsArray;
      return options
        .filter(v => v.includes(term) && !vals.includes(v))
        .map(v => ({ name: v }));
    };
  }

  get placeholder() {
    return this.autocompleteElement.placeholder;
  }
  set placeholder(value) {
    this.autocompleteElement.placeholder = value;
  }

  validate() {
    if (this.noValidate) {
      return true;
    }

    if (this.checkValidity()) {
      this.hideBalloon();
      return true;
    }

    this.showBalloon();
    this.autocompleteElement.focus();
    return false;
  }

  getValue() {
    return this.valueAsArray.join(',');
  }

  setValue(val) {
    for (let itemEl of this.allItemElements) {
      this.removeItem(itemEl);
    }
    this.addItems(val);
  }

  get valueAsArray() {
    return this.items.map(v => v.name);
  }

  checkValidity() {
    let is_valid = this.value;
    let el = this.editableListElement;
    if (is_valid) {
      el.removeAttribute('invalid');
      return true;
    }
    el.setAttribute('invalid', 'true');
    return false;
  }

  focus() {
    if (!this.readonly) {
      this.autocompleteElement.focus();
    }
  }

  showBalloon() {
    this.popupElement.open = true;
  }
  hideBalloon() {
    this.popupElement.open = false;
  }

  get hasFocus() {
    return this.editableListElement.hasAttribute('hasfocus');
  }
  set hasFocus(v) {
    v
      ? this.editableListElement.setAttribute('hasfocus', 'true')
      : this.editableListElement.removeAttribute('hasfocus');

    if (!v) {
      this.hideBalloon();
      setTimeout(() => {
        let value = this.value;
        if (value != this._value) {
          if (this.validate() && this.onchange) {
            this.onchange(value, this._value);
          }
          this._value = value;
        }
      }, 300);
    }
  }

  autocompleteUpdated() {
    return Promise.resolve(this.autocompleteElement.itemsUpdatePromise);
  }

  onFocus() {
    // plain style input is hidden and thus can't take focus, set @hasfocus
    // attribute to make it visible.
    if (this.hasFocus) {
      return;
    }
    this.hasFocus = true;
    this.hideBalloon();
    if (!this.readonly) {
      this.autocompleteElement.focus();
    }
  }

  onBlur(ev) {
    // remove @hasfocus if focus doesn't go into input.
    if (
      (this.hasFocus && !ev.relatedTarget) ||
      (!this.isItemElement(ev.relatedTarget) &&
        !this.autocompleteElement.isInputElement(ev.relatedTarget))
    ) {
      this.hasFocus = false;
    }
  }

  onInputFocus() {
    this.hasFocus = true;
    this._changed = false;
    if (this.onfocus) {
      this.onfocus();
    }
  }

  onInputBlur(ev) {
    if (!ev.relatedTarget || !this.isItemElement(ev.relatedTarget)) {
      this.hasFocus = false;
    }
    this.addItemsFromUI();
  }

  onInputKeyDown(ev) {
    switch (ev.key) {
      case ' ':
        if (this.breakOnSpace && this.autocompleteElement.value) {
          this.addItemsFromUI();
          ev.preventDefault();
        }
        break;

      case 'Tab':
        if (this.autocompleteElement.value) {
          this.addItemsFromUI();
          ev.preventDefault();
        }
        break;

      case 'Backspace':
      case 'ArrowLeft': {
        let items = this.allItemElements;
        if (items.length > 0 && this.autocompleteElement.value == '') {
          items[items.length - 1].focus();
          ev.preventDefault();
        }
        break;
      }

      default:
        break;
    }
  }

  onItemFocus() {
    this.hasFocus = true;
    if (this.onfocus) {
      this.onfocus();
    }
  }

  onItemKeyDown(ev) {
    let item = ev.target;
    switch (ev.key) {
      case 'ArrowRight': {
        let nextSibling = item.nextElementSibling;
        if (nextSibling) {
          if (this.isItemElement(nextSibling)) {
            nextSibling.focus();
          } else {
            this.autocompleteElement.focus();
          }
        }
        break;
      }

      case 'ArrowLeft': {
        let prevSibling = item.previousElementSibling;
        if (prevSibling && this.isItemElement(prevSibling)) {
          prevSibling.focus();
        }
        break;
      }

      case 'Backspace':
        this.removeItem(item);
        ev.preventDefault();
        break;

      default:
        break;
    }
  }

  addItemsFromUI() {
    let text = this.autocompleteElement.value;
    if (!text) {
      return null;
    }

    this.addItems(text);
    this.autocompleteElement.value = '';
    this.validate();

    return this.autocompleteElement.scheduleItemsUpdate();
  }

  addItemFromSelected(item) {
    assert(item, `[control:list] no item`);
    this.addItem(item);
    this.autocompleteElement.value = '';
    this.autocompleteElement.focus();
    this.validate();

    return this.autocompleteElement.scheduleItemsUpdate();
  }

  addItems(textOrArray) {
    if (typeof textOrArray == 'string') {
      let values = textOrArray.split(/[,;]/);
      for (let value of values) {
        let item_value = value.trim();
        if (item_value) {
          this.addItem(item_value);
        }
      }
    } else if (Array.isArray(textOrArray)) {
      for (let item of textOrArray) {
        this.addItem(item);
      }
    }
  }

  addItem(item) {
    if (typeof item == 'string') {
      item = { name: item };
    }

    this.items.push(item);

    let itemEl = this.ownerDocument.createElement('span');
    itemEl.setAttribute('tabindex', '-1');
    itemEl.className = 'editable-control-list-item';
    itemEl.innerHTML = `${item.name}<img src='/images/delete.png' class='button'
                        title='Delete'>`;

    itemEl.onkeydown = ev => this.onItemKeyDown(ev);
    itemEl.onfocus = ev => this.onItemFocus(ev);
    itemEl.onblur = ev => this.onBlur(ev);
    itemEl.querySelector('.button').onclick = () =>
      this.removeItem(itemEl, 'mouse-click');

    this.autocompleteElement.before(itemEl);

    this.updateStaticValue();
    this.onitemchange && this.onitemchange(this.value);
  }

  removeItem(itemEl, isMouseClick) {
    // Move focus if given item element is focused.
    if (itemEl.ownerDocument.activeElement == itemEl) {
      let prevSibling = itemEl.previousElementSibling;
      let nextSibling = itemEl.nextElementSibling;

      // Focus the autocomplete on item removal on mouse click.
      if (!isMouseClick && prevSibling && this.isItemElement(prevSibling)) {
        prevSibling.focus();
      } else if (
        !isMouseClick &&
        nextSibling &&
        this.isItemElement(nextSibling)
      ) {
        nextSibling.focus();
      } else {
        this.autocompleteElement.focus();
      }
    }

    let idx = Array.from(this.allItemElements).indexOf(itemEl);

    itemEl.parentNode.removeChild(itemEl);
    this.items.splice(idx, 1);

    this.autocompleteElement.scheduleItemsUpdate();
    this.updateStaticValue();
    this.onitemchange && this.onitemchange(this.value);
  }

  updateStaticValue() {
    this.valueElement.innerHTML = this.items.reduce((acc, v) => {
      let title = (v.title && `title='${v.title}'`) || '';
      let ico = (v.img && `<img src='${v.img}'>`) || '';
      let item = `
            <span class='editable-control-value-item'>
              ${ico}<span ${title}>${v.name}</span>
            </span>`;
      return `${acc} ${item}`;
    }, '');
  }

  static get selectors() {
    const AutocompleteSelectors = AutocompleteControl.selectors;
    const Autocomplete = 'autocomplete-control';
    return {
      AllItems: '.editable-control-list-item',
      Autocomplete,
      AutocompleteInput: `${Autocomplete} ${AutocompleteSelectors.Input}`,
      EditableList: '.editable-control',
      Label: '.editable-control-label',
      Popup: `${Autocomplete} ${AutocompleteSelectors.Popup}`,
      PopupItems: `${Autocomplete} ${AutocompleteSelectors.AllItems}`,
      PopupSelectedItem: `${Autocomplete} ${AutocompleteSelectors.SelectedItem}`,
      PopupItemAt: i => `${Autocomplete} ${AutocompleteSelectors.ItemAt(i)}`,
      Value: '.editable-control-value',
    };
  }

  static get elements() {
    return {
      allItemElements: this.selectors.AllItems,
      autocompleteElement: this.selectors.Autocomplete,
      editableListElement: this.selectors.EditableList,
      labelElement: this.selectors.Label,
      popupElement: this.selectors.Popup,
      valueElement: this.selectors.Value,
    };
  }

  isItemElement(el) {
    return (
      el instanceof Element &&
      el.classList.contains('editable-control-list-item')
    );
  }
}

window.customElements.define('list-control', ListControl);

export { ListControl };
